import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import { BellIcon, CheckIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const filterSelectionFragment = graphql(`
  fragment FilterSelectionFragment on SavedFilter {
    id
    name
    alertEnabled
  }
`);

interface FilterSelectionListProps {
  filters: FragmentOf<typeof filterSelectionFragment>[];
  selectedFilterId: string | null;
  onSelectFilter: (filterId: string) => void;
}

export const FilterSelectionList = ({
  filters,
  selectedFilterId,
  onSelectFilter,
}: FilterSelectionListProps) => {
  const { t } = useTranslation();

  if (filters.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        {t('保存されたフィルタはありません')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700">
        {t('上書きするフィルタを選択')}
      </div>
      <div className="space-y-1">
        {filters.map((filter) => {
          const savedFilter = readFragment(filterSelectionFragment, filter);
          const isSelected = selectedFilterId === savedFilter.id;

          return (
            <div
              key={savedFilter.id}
              onClick={() => onSelectFilter(savedFilter.id)}
              className={clsx(
                'flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors',
                isSelected
                  ? 'border border-rw-main/20 bg-rw-main/10'
                  : 'border border-transparent bg-gray-50 hover:bg-gray-100'
              )}
            >
              <div
                className={clsx(
                  'flex size-5 items-center justify-center rounded-full border-2',
                  isSelected
                    ? 'border-rw-main bg-rw-main'
                    : 'border-gray-300 bg-white'
                )}
              >
                {isSelected && <CheckIcon className="size-3 text-white" />}
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className={clsx(
                    'min-w-0 flex-1 truncate text-sm',
                    isSelected ? 'font-medium text-rw-main' : 'text-gray-700'
                  )}
                >
                  {savedFilter.name}
                </span>
                {savedFilter.alertEnabled && (
                  <BellIcon
                    className="size-4 shrink-0 text-yellow-500"
                    title={t('通知設定済み')}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

FilterSelectionList.fragments = {
  data: filterSelectionFragment,
};
